export async function withAbortTimeout<T>(
  request: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  parentSignal?: AbortSignal,
  errorMessage = "Workflow history request timed out",
): Promise<T> {
  if (parentSignal?.aborted) {
    throw parentSignal.reason ?? new DOMException("Request cancelled", "AbortError");
  }

  const controller = new AbortController();
  let rejectCancellation: (reason: unknown) => void = () => undefined;
  const cancellation = new Promise<never>((_resolve, reject) => {
    rejectCancellation = reject;
  });
  const abort = (reason: unknown) => {
    controller.abort(reason);
    rejectCancellation(reason);
  };
  const onParentAbort = () => abort(parentSignal?.reason ?? new DOMException("Request cancelled", "AbortError"));
  const timeout = setTimeout(
    () => abort(new Error(errorMessage)),
    timeoutMs,
  );

  parentSignal?.addEventListener("abort", onParentAbort, { once: true });

  try {
    return await Promise.race([request(controller.signal), cancellation]);
  } finally {
    clearTimeout(timeout);
    parentSignal?.removeEventListener("abort", onParentAbort);
  }
}
